import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styled from 'styled-components';

import { AutoTableProps, AutoTable } from '@/ui/AutoTable';
import { ButtonGroup } from '@/ui/ButtonGroup';
import { Skeleton } from '@/ui/Skeleton';
import { Table } from '@/ui/Table';
import { Button } from '@/ui/Button';
import { LearnerRow, useLearnersColumnDeclaration } from '@/features/journey/admin/hooks/useLearnersColumnsDeclaration';
import { JourneyRemoveLearnerModal } from '@/features/journey/admin/molecules/JourneyRemoveLearnerModal';
import { JourneyHeaderWithAction } from '@/features/journey/admin/atoms/JourneyHeaderWithActions';
import { useJourneyLearners, SortyBy } from '@/features/journey/admin/hooks/useJourneyLerners';
import { AddParticipantsModal } from '@/administration/pages/Journey/common/modals/AddParticipants';
import { useJourneyNavigate } from '@/administration/pages/Journey/utils/useJourneyNavigate';
import { useUpsertLearningJourneyCohorts } from '@/administration/pages/Journey/store/journeySession/useUpsertLearningJourneyCohorts';
import { useLearningJourneySession } from '@/administration/pages/Journey/store/journeySession/useLearningJourneySession';
import { ManagementEmptyStateAfterProcessed } from '@/features/management/manager/atoms/ManagementEmptyStateAfterProcessed';
import { ManagementEmptyStateNoData } from '@/features/management/manager/atoms/ManagementEmptyStateNoData';
import { ManagementSearchPhrase } from '@/features/management/manager/atoms/ManagementSearchPhrase'; // TODO (for later PRs) Make it a generic one, so we could use it in both places
import { ManagementStatusFilter } from '@/features/management/manager/atoms/ManagementStatusFilter';
import { useManagedTeams } from '@/features/management/manager/hooks/useManagedTeams';
import { useIsManagementContext } from '@/features/management/manager/hooks/useIsManagementContext';
import { SEARCH_PARAM_KEYS, usePageState } from '@/features/management/manager/hooks/usePageState';
import { useQueryNumberArrayState } from '@/hooks/useQueryState';
import { JourneyInsightsTabsParticipantsAddingButton } from '@/features/journey/admin/atoms/JourneyInsightsTabsParticipantsAddingButton';
import { JourneyAddTeamsModal } from '@/features/journey/admin/molecules/JourneyAddTeamsModal/JourneyAddTeamsModal';
import { JourneyInsightsSelectedParticipantsFloatingBar } from '@/features/journey/admin/atoms/JourneyInsightsSelectedParticipantsFloatingBar';
import { useGenerateLearningJourneyLearnersCsvMutationRemote } from '@/store/v2';

type Modal = 'addParticipantsModal' | 'removeParticipantModal' | 'addTeamsModal';

type ModalProps = {
  AddParticipants: React.ComponentProps<typeof AddParticipantsModal>;
  RemoveParticipant: React.ComponentProps<typeof JourneyRemoveLearnerModal>;
  AddTeams: React.ComponentProps<typeof JourneyAddTeamsModal>;
};

const PAGE_SIZE = 20;

const ContainerStyled = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const LoadingState = (): React.ReactElement => (
  <Table>
    <Table.Header>
      <Table.HeaderCell expand>
        <Skeleton height={16} width={57} />
      </Table.HeaderCell>
      {[...Array(3).keys()].map((j) => (
        <Table.HeaderCell key={j}>
          <div style={{ width: 150 }}>
            <Skeleton height={16} width={57} />
          </div>
        </Table.HeaderCell>
      ))}
      <Table.HeaderCell shrink>
        <Skeleton height={16} width={57} />
      </Table.HeaderCell>
    </Table.Header>

    <Table.Body>
      {[...Array(4).keys()].map((index) => (
        <Table.Row key={index}>
          {[...Array(5).keys()].map((index) => (
            <Table.Cell key={index}>
              <Skeleton height={16} width={57} />
            </Table.Cell>
          ))}
        </Table.Row>
      ))}
    </Table.Body>
  </Table>
);

export const JourneyInsightsTabsParticipantsContent = ({ spaceId }: { spaceId: number }): JSX.Element => {
  const { journeyId } = useParams<{ journeyId: string }>();
  const { navigate404, navigateToImportLearners } = useJourneyNavigate();
  const [selectedTeamIds] = useQueryNumberArrayState(SEARCH_PARAM_KEYS.SELECTED_TEAM_IDS, []);
  const { t } = useTranslation('common');
  const [selectedParticipantsIds, setSelectedParticipantsIds] = useState<number[]>([]);
  const isManagementContext = useIsManagementContext();
  const [participantToRemoveInfo, setParticipantToRemoveInfo] = useState<
    | {
        id: number;
        name: string;
      }
    | undefined
  >();

  const [activeModal, setActiveModal] = useState<Modal | null>(null);

  const [downloadCsvLink, { loading: downloadCsvLinkLoading }] = useGenerateLearningJourneyLearnersCsvMutationRemote();
  const { upsertLearningJourneyCohorts } = useUpsertLearningJourneyCohorts();

  const {
    searchPhrase,
    setSearchPhrase,
    selectedStatus,
    setSelectedStatus,
    sortBy,
    sortOrder,
    onSortChange,
    page,
    setPage,
    onClearFilterState,
  } = usePageState<SortyBy>('name');

  const areFiltersApplied = Boolean(searchPhrase.length || selectedStatus || selectedTeamIds?.length);

  const teams = useManagedTeams({
    skip: !isManagementContext,
  });

  const { journeyLearners, loading, error, total } = useJourneyLearners({
    journeyURN: `urn:collegial:self:journey:${journeyId}`,
    sortBy,
    sortOrder,
    status: selectedStatus,
    searchPhrase: searchPhrase.length ? searchPhrase : undefined,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
    teamIds: !!selectedTeamIds?.length ? selectedTeamIds : isManagementContext ? teams?.data?.map((team) => team.id) : undefined,
  });

  const handleAllParticipantsSelectToggled = (areAllSelected: boolean) => {
    if (areAllSelected) {
      setSelectedParticipantsIds([]);
    } else {
      setSelectedParticipantsIds(journeyLearners?.map((learner) => learner.id));
    }
  };

  const columnDeclaration = useLearnersColumnDeclaration({
    areAllParticipantsSelected:
      selectedParticipantsIds?.length === journeyLearners?.length && selectedParticipantsIds?.length !== 0,
    onAllParticipantsSelectToggled: handleAllParticipantsSelectToggled,
  });

  // TODO: Remove this once we have the API for this
  const { journeySession } = useLearningJourneySession({ id: Number(journeyId) });
  const participantsIds = journeySession?.cohort?.learners?.map((l) => l.userId) || [];

  const openAddParticipantsModal = () => {
    setActiveModal('addParticipantsModal');
  };

  const openAddTeamsModal = () => {
    setActiveModal('addTeamsModal');
  };

  const handleImportLearnersClick = () => navigateToImportLearners(Number(journeyId));

  const openRemoveParticipantModal = () => {
    setActiveModal('removeParticipantModal');
  };

  const handleDownloadCsvClick = async () => {
    const { data } = await downloadCsvLink({
      variables: {
        input: {
          teamIds: isManagementContext ? teams?.data?.map((team) => team.id) : undefined,
          urn: `urn:collegial:self:journey:${journeyId}`,
          spaceId,
        },
      },
    });
    const csvLink = data?.generateLearningJourneyLearnersCSV?.presignedURL;
    if (csvLink) {
      window.open(csvLink, '_blank');
    }
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const addParticipantsModalProps: ModalProps['AddParticipants'] = {
    open: activeModal === 'addParticipantsModal',
    onClose: handleModalClose,
    onSave: async (selectedUserIds) => {
      await upsertLearningJourneyCohorts(Number(journeyId), [...selectedUserIds, ...participantsIds]);
    },
    alreadyAddedUserIds: participantsIds,
  };

  const addTeamsModalProps: ModalProps['AddTeams'] = {
    open: activeModal === 'addTeamsModal',
    onClose: handleModalClose,
    journeyId: Number(journeyId),
  };

  const removeParticipantsModalProps: ModalProps['RemoveParticipant'] = {
    open: activeModal === 'removeParticipantModal',
    onCancel: handleModalClose,
    targetParticipantInfo: participantToRemoveInfo,
    onRemove: async (id) => {
      await upsertLearningJourneyCohorts(
        Number(journeyId),
        participantsIds.filter((participantId) => participantId !== id),
      );
    },
  };

  if (error) navigate404();

  const pageCount = Math.ceil((total ?? 0) / PAGE_SIZE);

  const handleRemoveClick = ({ id: participantId, name: participantName }: { id: number; name: string }) => {
    setParticipantToRemoveInfo({
      id: participantId,
      name: participantName,
    });
    openRemoveParticipantModal();
  };

  const handleToggleSelected = (id: number) => {
    if (selectedParticipantsIds?.includes(id)) {
      setSelectedParticipantsIds(selectedParticipantsIds.filter((participantId) => participantId !== id));
    } else {
      setSelectedParticipantsIds([...selectedParticipantsIds, id]);
    }
  };

  const handleRemovalDiscardClick = () => {
    setSelectedParticipantsIds([]);
  };

  const tableProps: AutoTableProps<LearnerRow, SortyBy> = {
    rows: journeyLearners.map((p, _) => ({
      ...p,
      onRemove: handleRemoveClick,
      selected: selectedParticipantsIds?.includes(p.id),
      onToggleSelected: handleToggleSelected,
    })),
    rowKey: (participant) => participant.id,
    columnDeclaration,
    pageCount,
    page,
    onPageChange: (event) => {
      setPage(event);
    },
    currentSortKey: sortBy,
    currentSortOrder: sortOrder,
    onSort: onSortChange,
  };

  return (
    <ContainerStyled>
      <JourneyAddTeamsModal {...addTeamsModalProps} />
      <AddParticipantsModal {...addParticipantsModalProps} />
      <JourneyRemoveLearnerModal {...removeParticipantsModalProps} />
      <>
        <JourneyHeaderWithAction
          title={t('edit-learners-page-title', { ns: 'journey' })}
          counter={total ?? 0}
          customAction={
            <ButtonGroup>
              <Button variant="secondary" $loading={downloadCsvLinkLoading} onClick={handleDownloadCsvClick}>
                Download (.csv)
              </Button>
              {!isManagementContext && (
                <JourneyInsightsTabsParticipantsAddingButton
                  openAddParticipantsModal={openAddParticipantsModal}
                  openAddTeamsModal={openAddTeamsModal}
                  onImportLearnersClick={handleImportLearnersClick}
                />
              )}
            </ButtonGroup>
          }
        />
        <ActionsStyled>
          <ManagementSearchPhrase
            value={searchPhrase}
            onChange={(phrase) => {
              setSearchPhrase(phrase);
            }}
            placeholder={t('edit-learners-table-search-placeholder', { ns: 'journey' })}
          />
          <ButtonGroup>
            <ManagementStatusFilter
              selectedStatus={selectedStatus}
              onChange={(status) => {
                setSelectedStatus && setSelectedStatus(status);
              }}
              onTrailingIconClick={() => onClearFilterState('status')}
              statuses={['completed', 'not completed']}
            />
          </ButtonGroup>
        </ActionsStyled>
        {loading && <LoadingState />}
        {!loading && journeyLearners.length > 0 && <AutoTable<LearnerRow, SortyBy> {...tableProps} />}
        {!loading && journeyLearners.length === 0 && !areFiltersApplied && <ManagementEmptyStateNoData />}
        {!loading && journeyLearners.length === 0 && areFiltersApplied && (
          <ManagementEmptyStateAfterProcessed onFiltersClear={() => onClearFilterState('clear all')} />
        )}
      </>
      {selectedParticipantsIds?.length > 0 && (
        <JourneyInsightsSelectedParticipantsFloatingBar
          onDiscardClick={handleRemovalDiscardClick}
          journeyId={Number(journeyId)}
          selectedParticipantsIds={selectedParticipantsIds}
        />
      )}
    </ContainerStyled>
  );
};
